import { Component, Vue, Watch, Prop } from 'vue-property-decorator'
import { List } from '@/models/list/list';
import { DeliveryServer } from '@/models/delivery-server/delivery-server';
import { TrackingDomain } from '@/models/tracking-domain/tracking-domain';
import { TestCase } from '@/models/test-case/testcase';
import { testCaseService } from '@/services/testcase-service';
import PreviewTestcaseComponent from './components/preview-testcase/preview-testcase.vue';
import TestcaseGeneratorComponent from './components/testcase-generator/testcase-generator';
import { listService } from '@/services/list.service';
import { trackingDomainsService } from '@/services/tracking-domains.service';
import { deliveryServerService } from '@/services/delivery-server.service';
import { appSettingsModule } from '@/store/app-settings-module';
import { TestCaseResponse } from '@/models/test-case/test-case-response';
import { isStringNull } from '@/shared/utils/utils';
import { Settings } from '@/models/settings/settings';
import { settingsService } from '@/services/settings-service';
import { TestCaseCreateOptions } from '@/models/test-case/test-case-create-options';

@Component({
    components: {
        'testcase-generator': TestcaseGeneratorComponent,
        'preview-testcase': PreviewTestcaseComponent
    }
})
export default class CreateTestCaseComponent extends Vue {
  
    @Prop() copyFromId: number

    lists: List[] = []
    deliveryServers: DeliveryServer[] = []
    trackingDomains: TrackingDomain[] = []
    settings: Settings = null
    usedTrackingDomains: number[] = []

    copyFrom: TestCaseResponse = null
    testCase: TestCase = null
    savingTestCase: boolean = false
    savingAndSendingTestCase: boolean = false
    loading: boolean = true

    invalidFields: string[] = []

    get mailWizzInstanceId() { return appSettingsModule.activeMailWizzId }

    @Watch('mailWizzInstanceId')
    async mailWizzInstanceIdChanged(){
        await this.refresh()
    }

    async mounted() {
        await this.refresh()
    }

    async refresh() {
        this.testCase = null;
        try {
            this.loading = true;
            [ this.lists, this.trackingDomains, this.deliveryServers, this.settings, this.copyFrom, this.usedTrackingDomains ] = await Promise.all([
                listService.getAll<List>(),
                trackingDomainsService.getAll<TrackingDomain>(),
                <any>deliveryServerService.getAll<DeliveryServer>(),
                settingsService.get<Settings>(),
                this.copyFromId > 0 ? testCaseService.getById(this.copyFromId) : Promise.resolve(null),
                trackingDomainsService.getLocked()
            ]);

            this.trackingDomains = this.trackingDomains.filter(x => x.active)

            if (this.copyFrom && this.copyFrom.mailWizzInstanceId != this.mailWizzInstanceId) {
                appSettingsModule.setActiveMailWizzId(this.copyFrom.mailWizzInstanceId)
            }
        }
        finally {
            this.loading = false
        }
    }
    
    testCaseGenerated(testCase: TestCase) {
        this.testCase = testCase
        this.$root.$emit('bv::toggle::collapse', 'generateTestcase')
        this.$root.$emit('bv::toggle::collapse', 'previewTestcase')
    }

    testCaseChanged(testCase: TestCase) {
        Object.assign(this.testCase, testCase)
    }

    async save() {
      await this.saveTestcase({ delaySend: true }, saving => this.savingTestCase = saving);
    }

    async saveAndSend() {
      await this.saveTestcase({ delaySend: false }, saving => this.savingAndSendingTestCase = saving);
    }

    async saveTestcase(options: TestCaseCreateOptions = {}, setProgress: (boolean) => void) {

        if (!this.validate()) return

        setProgress(true)
        try {
            await testCaseService.createTestCase(this.testCase, options)
            this.$router.push('/testcases');
        }
        finally {
            setProgress(false)
        }
    }

    validate() : boolean {
        // Check that there are no identical subjects
        const allSubjects = this.testCase.campaigns.map(x => x.subject)
        const duplicatedSubjects = allSubjects.filter((subject, index) => allSubjects.indexOf(subject) != index)
        if (duplicatedSubjects.length > 0) {
            this.$bvModal.msgBoxOk("All Subjects should be unique to match Gmass results correctly")
            return false
        }

        this.invalidFields.splice(0, this.invalidFields.length)

        for (let i = 0; i < this.testCase.campaigns.length; i++) {
            const campaign = this.testCase.campaigns[i];
            if (isStringNull(campaign.subject)) {
                this.invalidFields.push(`subject${i}`)
            }
            if (isStringNull(campaign.body)) {
                this.invalidFields.push(`body${i}`)
            }
            if (isStringNull(campaign.fromEmail)) {
                this.invalidFields.push(`fromEmail${i}`)
            }
            if (isStringNull(campaign.fromName)) {
                this.invalidFields.push(`fromName${i}`)
            }
            if (isStringNull(campaign.replyTo)) {
                this.invalidFields.push(`replyTo${i}`)
            }
            if (!campaign.deliveryServer || !campaign.deliveryServer.mailWizzEntityId) {
                this.invalidFields.push(`deliveryServer${i}`)
            }
            if (!campaign.list || !campaign.list.mailWizzEntityId) {
                this.invalidFields.push(`list${i}`)
            }
            if (!campaign.trackingDomain || !campaign.trackingDomain.mailWizzEntityId) {
                this.invalidFields.push(`trackingDomain${i}`)
            }
            if (campaign.service === 'glock' && (!campaign.providers || campaign.providers.length === 0)) {
                this.invalidFields.push(`providers${i}`)
            }
        }

        if (this.invalidFields.length > 0) {
            this.$bvModal.msgBoxOk("There are validation errors in 'Preview & and Edit Campaigns' section")
            return false
        }

        return true
    }

    back() {
        this.$router.push('/testcases')
    }
}